import { z as baseZod } from 'zod'

import { FlowExtensionParamWidgetTypes, type FrGroupToken } from './widget-types'

// The platform's own file-storage option groups, declared once and handed to services through
// `Flowrunner.z` (see ./zod.ts) — the v2 counterpart of the built-in `FilesUploadOptions` /
// `FilesListOptions` / `FilesGetOptions` / `FilesDeleteOptions` param types the v1 JSDoc parser registers
// (`jsdoc-parser/src/custom-type-defs/files.ts`).
//
// **Why this is shipped rather than copied.** A v1 service names the type and gets the platform's values,
// labels, descriptions and defaults; reword a label here and every v1 service picks it up on the next parse.
// Without an equivalent, every v2 service that stores a file owns a private copy of the same ten lines —
// nothing propagates, and a copy that drifts publishes a field the runtime does not have. That is not
// hypothetical: the v1 services that shadow the built-in with their own typedef publish a `directory` option
// `uploadFile` silently drops.
//
// **The `include` argument.** v1 subsets a group at publish time with `@paramDef {… "include":["scope"]}`,
// and 706 of the catalog's 729 `@paramDef`s do. The factories below take that same list, so an existing
// `@paramDef` translates by inspection and the published param comes out identical. No argument means every
// field, matching v1's no-`include` behaviour.
//
// Two display differences from v1 are expected and harmless, because both are widgets the console resolves
// to the same control: a plain string publishes no `uiComponent` at all (the v2 model omits display defaults
// so they can be reworded without a redeploy — `SINGLE_LINE_TEXT` is the console's fallback), and a number
// publishes `NUMERIC_STEPPER` where v1 hand-wrote the legacy `NUMERIC`. `scope`, the only field the catalog
// actually exposes, is byte-identical — see `__tests__/files-options-schema.test.ts`.
//
// ⚠️ Imports `zod` directly, never `./zod`, to keep the dependency one-way. The authoring plugins used below
// (`.label()`) are installed on zod's prototype by ./zod.ts, which imports THIS module — safe because a
// factory can only be reached through the namespace that module builds, so the plugins are always in place
// by the time one runs.

// Kept in step with `FileScope` in ../../common/context-files.ts, which cannot be imported here: it pulls in
// the HTTP client, and the params framework is compiled into the console's browser bundle. A test asserts
// the two agree.
const SCOPE_VALUES = ['WORKSPACE', 'FLOW', 'EXECUTION'] as const

// `scope` is the one field shared by all four groups — and the only one the catalog exposes to flow authors.
const scope = () => baseZod.enum(SCOPE_VALUES)
  .label('Scope')
  .describe('Storage scope: WORKSPACE (shared across flows), FLOW (this flow only), EXECUTION (this run only)')
  .default('FLOW')

const ttl = () => baseZod.number()
  .label('URL TTL (seconds)')
  .describe('How long the download URL remains valid')
  .optional()

const generateUrl = () => baseZod.boolean()
  .label('Generate download URL')
  .describe('Include a pre-signed download URL in the response')
  .default(true)

// A group is a table of FACTORIES, not of schemas: each call hands the service a fresh object, so one
// service's `.optional()` or `.describe()` can never reach another's.
type FieldTable = Record<string, () => baseZod.ZodType>

const UPLOAD_FIELDS = {
  scope,
  filename: () => baseZod.string()
    .label('Filename')
    .describe('Custom filename (defaults to the original name if omitted)')
    .optional(),
  ttl,
  objectTtl: () => baseZod.number()
    .label('File TTL (seconds)')
    .describe('How long the file is kept in storage (omit for permanent storage)')
    .optional(),
  overwrite: () => baseZod.boolean()
    .label('Overwrite existing')
    .describe('Replace the file if one with the same name already exists')
    .optional(),
  generateUrl,
} satisfies FieldTable

const LIST_FIELDS = {
  scope,
  limit: () => baseZod.number()
    .label('Limit')
    .describe('Maximum number of files to return')
    .optional(),
  ttl: () => baseZod.number()
    .label('URL TTL (seconds)')
    .describe('How long the download URLs remain valid')
    .optional(),
  generateUrl: () => baseZod.boolean()
    .label('Generate download URLs')
    .describe('Include pre-signed download URLs in the response')
    .default(true),
  cursor: () => baseZod.string()
    .label('Cursor')
    .describe('Pagination cursor from a previous response')
    .optional(),
} satisfies FieldTable

const GET_FIELDS = {
  scope,
  ttl,
  generateUrl,
} satisfies FieldTable

const DELETE_FIELDS = {
  scope,
} satisfies FieldTable

// The object a factory returns: the picked fields, each typed as its own factory declares it.
type Group<T extends FieldTable, K extends keyof T> = baseZod.ZodObject<{ [P in K]: ReturnType<T[P]> }>

/**
 * Build one group, optionally subset to `include`.
 *
 * Two details reproduce v1 exactly (`jsdoc-parser/src/index.ts` `buildParam`): the result keeps the
 * CANONICAL field order rather than the order of `include` — so `['filename', 'scope']` still publishes
 * `scope` first — and an empty list is treated as no list, i.e. every field.
 *
 * An unknown name THROWS, where v1 silently published an empty object. A typo there is invisible until a
 * flow author opens the block and finds no settings at all; here it fails while the service registers, which
 * is the only moment anyone is looking.
 */
function group<T extends FieldTable, K extends keyof T & string>(
  typeName: string,
  widget: FrGroupToken,
  table: T,
  include?: readonly K[]
): Group<T, K> {
  const names = Object.keys(table)

  if (include?.length) {
    const unknown = include.filter(name => !names.includes(name))

    if (unknown.length) {
      throw new Error(
        `z.${ typeName }(): unknown field${ unknown.length > 1 ? 's' : '' } ${ unknown.map(n => `'${ n }'`).join(', ') }. ` +
        `Available fields: ${ names.join(', ') }.`
      )
    }
  }

  const selected = include?.length ? names.filter(name => include.includes(name as K)) : names

  const shape = Object.fromEntries(selected.map(name => [name, table[name]()]))

  // The group names itself in the published model (`uiComponent: { type: 'FILE_UPLOAD_OPTIONS' }`), so a
  // consumer can tell the platform's own options object from an anonymous one a service declared and render
  // it accordingly. See ../model/field-mapper.ts, which publishes it at every depth.
  return baseZod.object(shape).meta({ frWidget: widget }) as Group<T, K>
}

export type FilesUploadOptionsField = keyof typeof UPLOAD_FIELDS & string
export type FilesListOptionsField = keyof typeof LIST_FIELDS & string
export type FilesGetOptionsField = keyof typeof GET_FIELDS & string
export type FilesDeleteOptionsField = keyof typeof DELETE_FIELDS & string

/** Options for `ctx.files.uploadFile()`. `z.filesUploadOptions(['scope'])` is the form the catalog uses. */
export function filesUploadOptions<K extends FilesUploadOptionsField = FilesUploadOptionsField>(
  include?: readonly K[]
): Group<typeof UPLOAD_FIELDS, K> {
  return group('filesUploadOptions', FlowExtensionParamWidgetTypes.FILE_UPLOAD_OPTIONS as FrGroupToken, UPLOAD_FIELDS, include)
}

/** Options for `ctx.files.list()`. */
export function filesListOptions<K extends FilesListOptionsField = FilesListOptionsField>(
  include?: readonly K[]
): Group<typeof LIST_FIELDS, K> {
  return group('filesListOptions', FlowExtensionParamWidgetTypes.FILE_LIST_OPTIONS as FrGroupToken, LIST_FIELDS, include)
}

/** Options for `ctx.files.get()`. */
export function filesGetOptions<K extends FilesGetOptionsField = FilesGetOptionsField>(
  include?: readonly K[]
): Group<typeof GET_FIELDS, K> {
  return group('filesGetOptions', FlowExtensionParamWidgetTypes.FILE_GET_OPTIONS as FrGroupToken, GET_FIELDS, include)
}

/** Options for `ctx.files.delete()`. */
export function filesDeleteOptions<K extends FilesDeleteOptionsField = FilesDeleteOptionsField>(
  include?: readonly K[]
): Group<typeof DELETE_FIELDS, K> {
  return group('filesDeleteOptions', FlowExtensionParamWidgetTypes.FILE_DELETE_OPTIONS as FrGroupToken, DELETE_FIELDS, include)
}

// Exported for the test that keeps this table and `FileScope` in step.
export { SCOPE_VALUES }
